// Web push (Firebase Cloud Messaging). The server (functions: sendPushForNotification) sends a push for every
// in-app notification to the tokens saved on user/{uid}.fcmTokens — the iOS app writes there too.
//
// Setup: generate the Web Push key pair in the Firebase console and set `vapidKey` in firebase.js
// (or VUE_APP_FIREBASE_VAPID_KEY in .env), then rebuild.
import { firebaseApp, db, vapidKey } from "@/firebase";
import { getMessaging, getToken, deleteToken, isSupported } from "firebase/messaging";
import { doc, setDoc, arrayUnion, arrayRemove } from "firebase/firestore";

const VAPID_KEY = vapidKey;

export async function pushSupported() {
  try {
    return typeof Notification !== "undefined" && "serviceWorker" in navigator && (await isSupported());
  } catch (e) { return false; }
}

export function pushPermission() {
  return typeof Notification === "undefined" ? "unsupported" : Notification.permission; // granted | denied | default
}

async function currentToken() {
  if (!VAPID_KEY) throw new Error("Push isn't configured yet (add the Web Push key to firebase.js).");
  const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  await navigator.serviceWorker.ready;
  return getToken(getMessaging(firebaseApp), { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
}

/** Must be called from a click (browsers require a user gesture to ask for permission). */
export async function enablePush(uid) {
  if (!(await pushSupported())) throw new Error("This browser doesn't support push notifications.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notifications are blocked — allow them in your browser's site settings.");
  const token = await currentToken();
  if (!token) throw new Error("Couldn't get a push token. Please try again.");
  await setDoc(doc(db, "user", uid), { fcmTokens: arrayUnion(token) }, { merge: true });
  return token;
}

/** Keeps this device's token fresh when permission was already granted (call after sign-in). */
export async function syncPushToken(uid) {
  try {
    if (!uid || !VAPID_KEY || pushPermission() !== "granted" || !(await pushSupported())) return;
    const token = await currentToken();
    if (token) await setDoc(doc(db, "user", uid), { fcmTokens: arrayUnion(token) }, { merge: true });
  } catch (e) { console.warn("push sync failed", e); }
}

export async function disablePush(uid) {
  try {
    const token = await currentToken();
    if (token) await setDoc(doc(db, "user", uid), { fcmTokens: arrayRemove(token) }, { merge: true });
    await deleteToken(getMessaging(firebaseApp));
  } catch (e) { console.warn("disable push failed", e); }
}